import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { useLocation, useNavigate } from 'react-router-dom';
import { Virtuoso } from 'react-virtuoso';
import useApartmentFilterNotification, {
  ApartmentFilterNotification,
} from '../../api/hooks/use-apartment-filter-notification';
import useApartmentNotification, {
  ApartmentNotification,
} from '../../api/hooks/use-apartment-notification';
import { changeNotificationStatus, deleteFilterNotificationStatus } from '../../api';
import { MarketTypes } from '../../api/types';
import { BuyApartmentFlowPaths, NotificationFlowPaths } from '../../routes';
import { ITEMS_PER_PAGE } from '../../lib/constants';
import ApartmentListItem, {
  APARTMENT_LIST_ITEM_HEIGHT,
} from '../../components/apartment-list-item';
import { ApartmentNavigateProps } from '../apartment';
import LoadMoreVirtualListFooter from '../../components/load-more-virtual-list-footer';
import ErrorMessage from '../../components/error-message';
import Loader from '../../components/loader';
import NotFound from '../../components/not-found';
import ApartmentFilterListItem from '../../components/apartment-filter-list-item';
import { NotificationSecondaryFilter } from './ui/NotificationSecondaryFilter';
import TabsScreen from '../../components/tabs-screen';
import IconButton from '../../components/icon-button';
import { ButtonProps } from '../../components/button';
import DeleteModal from '../../components/delete-modal';
import bellFilled from '../../assets/images/bell-filled.svg';
import bell from '../../assets/images/bell.svg';
import styles from '../choose-apartment-complex/styles.module.scss';
import useBoundStore from 'store';

export const NotificationScreen = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState(
    useLocation()?.state?.defaultTab ? useLocation()?.state?.defaultTab : 0,
  );
  const user = useBoundStore((s) => s.user);
  const isClient = useMemo(() => user?.type === 'Client', [user]);
  const isSecondaryMode = tab === 2;

  const buttons: ButtonProps[] = [
    {
      variantHeight: "more",
      variant: isSecondaryMode ? 'secondary' : 'default',
      label: isSecondaryMode
        ? 'Добавить уведомление по фильтрам вторички'
        : 'Добавить уведомление по фильтрам первички',
      onClick: () =>
        navigate(
          isSecondaryMode
            ? NotificationFlowPaths.notificationCreateFilterSecondary
            : NotificationFlowPaths.notificationCreateFilter,
        ),
    },
  ];

  return (
    <div>
      <TabsScreen
        buttons={tab && !isClient ? buttons : undefined}
        onTabChange={setTab}
        title={'Уведомления'}
        footerClassName={styles.footer}
        defaultTab={tab}
        tabs={[
          {
            title: 'По обьектам',
            content: <NotificationPersonal />,
          },
          { title: 'По фильтрам первички', content: <NotificationFilter /> },
          { title: 'По фильтрам вторички', content: <NotificationSecondaryFilter /> },
        ]}
        onBackButtonClick={() => navigate(BuyApartmentFlowPaths.main)}
      />
    </div>
  );
};

/* ------------------------------------------------------------------------------------ */

const NotificationPersonal = () => {
  const navigate = useNavigate();

  const {
    error,
    total,
    data: notification,
    mutate,
    setSize,
    isLoadingMore,
    isInitialLoading,
  } = useApartmentNotification();

  const handleChangeNotification =
    (apartment: ApartmentNotification) => async (e: React.MouseEvent) => {
      e.stopPropagation();

      await changeNotificationStatus(apartment, mutate);
    };

  return error && notification.length === 0 ? (
    <ErrorMessage className={styles.margined} message={error} />
  ) : isInitialLoading ? (
    <Loader className={styles.margined} />
  ) : notification.length > 0 ? (
    <>
      <Virtuoso
        useWindowScroll
        context={{
          total,
          loading: isLoadingMore,
          per: ITEMS_PER_PAGE,
          loadedNum: notification.length,
          loadMore: () => setSize((s) => s + 1),
        }}
        data={notification}
        style={{}}
        className={styles.container}
        defaultItemHeight={APARTMENT_LIST_ITEM_HEIGHT}
        itemContent={(index, apartment) => (
          <ApartmentListItem
            {...apartment}
            onClick={() =>
              navigate(
                apartment?.marketTypes === MarketTypes.SECONDARY
                  ? BuyApartmentFlowPaths.apartmentSecondary
                  : BuyApartmentFlowPaths.apartment,
                {
                  state: { id: apartment.id } as ApartmentNavigateProps,
                },
              )
            }
            leftIcon={
              <IconButton
                onClick={handleChangeNotification(apartment)}
                icon={apartment.notificationEnabled ? bellFilled : bell}
              />
            }
            className={clsx(index !== 3 - 1 && styles.border)}
          />
        )}
        components={{ Footer: LoadMoreVirtualListFooter }}
      />
    </>
  ) : (
    <NotFound />
  );
};

/* ------------------------------------------------------------------------------------ */

interface NotificationFilterProps {
  isSecondaryMode?: boolean;
}

const NotificationFilter: React.FC<NotificationFilterProps> = ({ isSecondaryMode }) => {
  const navigate = useNavigate();
  const {
    error,
    total,
    mutate,
    data: filterNotification,
    setSize,
    isLoadingMore,
    isInitialLoading,
  } = useApartmentFilterNotification();

  const [isShowDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [filterId, setFilterId] = useState<number | null>(null);
  const setLastSelectedFacilityList = useBoundStore((s) => s.setLastSelectedFacilityList);

  const handleRedirectFilterItem = (filterGroup: ApartmentFilterNotification) => {
    setLastSelectedFacilityList({ notificationId: filterGroup.id });
    navigate(NotificationFlowPaths.notificationFilterItem, { state: { id: filterGroup.id } });
  };

  const handleDeleteFilterNotification = async (filterGroupId: number) => {
    await deleteFilterNotificationStatus(filterGroupId, mutate);
  };

  return error && filterNotification?.length === 0 ? (
    <ErrorMessage className={styles.margined} message={error} />
  ) : isInitialLoading ? (
    <Loader className={styles.margined} />
  ) : filterNotification.length > 0 ? (
    <>
      {isShowDeleteModal && (
        <DeleteModal
          isOpen={true}
          onDelete={() => !!filterId && handleDeleteFilterNotification(filterId)}
          onClose={() => setShowDeleteModal(false)}
        />
      )}
      <Virtuoso
        style={{ height: `100vh` }}
        context={{
          total,
          loading: isLoadingMore,
          per: ITEMS_PER_PAGE,
          loadedNum: filterNotification.length,
          loadMore: () => setSize((s) => s + 1),
        }}
        data={filterNotification}
        itemContent={(index, filterGroup) => (
          <ApartmentFilterListItem
            onClick={() => handleRedirectFilterItem(filterGroup)}
            filterGroup={filterGroup}
            leftIcon={
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDeleteModal(true);
                  setFilterId(filterGroup?.id);
                }}
                icon={bellFilled}
              />
            }
          />
        )}
        components={{ Footer: LoadMoreVirtualListFooter }}
      />
    </>
  ) : (
    <NotFound />
  );
};
